import axios from 'axios';
import { getApiUrl } from '../config/environment';

// Usar getApiUrl com endpoint para garantir construção correta da URL
const api = axios.create({
  baseURL: getApiUrl('kanban'),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Adicionar user-id do localStorage se disponível
    const userId = localStorage.getItem('userId');
    if (userId) {
      config.headers['x-user-id'] = userId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratamento de erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Erro com resposta do servidor
      console.error('Erro na resposta do servidor Kanban:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        fullURL: `${error.config?.baseURL}${error.config?.url}`,
      });
    } else if (error.request) {
      // Erro na requisição (sem resposta)
      console.error('Erro na requisição Kanban (sem resposta):', {
        message: error.message,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
      });
    } else {
      // Erro ao configurar a requisição
      console.error('Erro ao configurar requisição Kanban:', error.message);
    }
    return Promise.reject(error);
  }
);

// ============================================
// INTERFACES
// ============================================

export interface KanbanBoard {
  id: string;
  name: string;
  description?: string;
  background_color: string;
  background_image_url?: string;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  last_activity_at: string;
  lists?: KanbanList[];
  labels?: KanbanLabel[];
}

export interface KanbanList {
  id: string;
  board_id: string;
  name: string;
  position: number;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  cards?: KanbanCard[];
}

export interface KanbanCard {
  id: string;
  list_id: string;
  board_id: string;
  title: string;
  description?: string;
  position: number;
  cover_image_url?: string;
  due_date?: string | null;
  is_completed: boolean;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  client_id?: string;
  client?: {
    id: string;
    company_name: string;
    contact_name: string;
    email: string;
  };
  members?: string[];
  labels?: string[];
  checklists?: KanbanChecklist[];
  comments?: KanbanComment[];
  attachments?: KanbanAttachment[];
  activities?: KanbanActivity[];
}

export interface KanbanLabel {
  id: string;
  board_id: string;
  name: string;
  color: string;
  created_at: string;
}

export interface KanbanChecklist {
  id: string;
  card_id: string;
  title: string;
  position: number;
  created_at: string;
  updated_at: string;
  kanban_checklist_items?: KanbanChecklistItem[];
}

export interface KanbanChecklistItem {
  id: string;
  checklist_id: string;
  text: string;
  is_completed: boolean;
  position: number;
  created_at: string;
  updated_at: string;
}

export interface KanbanComment {
  id: string;
  card_id: string;
  user_id: string;
  text: string;
  created_at: string;
  updated_at: string;
  user?: {
    id: string;
  };
}

export interface KanbanAttachment {
  id: string;
  card_id: string;
  file_name: string;
  file_url: string;
  file_type?: string;
  file_size?: number;
  uploaded_by?: string;
  created_at: string;
}

export interface KanbanActivity {
  id: string;
  board_id: string;
  card_id?: string;
  list_id?: string;
  user_id?: string;
  action_type: string;
  action_data?: any;
  created_at: string;
}

// ============================================
// API FUNCTIONS - BOARDS
// ============================================

export const kanbanService = {
  // Boards
  getBoards: async (): Promise<KanbanBoard[]> => {
    try {
      const response = await api.get('/boards');
      return response.data.data || [];
    } catch (error: any) {
      console.error('Erro ao buscar quadros do Kanban:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
        baseURL: error.config?.baseURL,
      });
      throw error;
    }
  },

  getBoard: async (id: string): Promise<KanbanBoard> => {
    const response = await api.get(`/boards/${id}`);
    return response.data.data;
  },

  createBoard: async (data: {
    name: string;
    description?: string;
    background_color?: string;
    background_image_url?: string;
  }): Promise<KanbanBoard> => {
    const response = await api.post('/boards', data);
    return response.data.data;
  },

  updateBoard: async (
    id: string,
    data: {
      name?: string;
      description?: string;
      background_color?: string;
      background_image_url?: string;
      is_archived?: boolean;
    }
  ): Promise<KanbanBoard> => {
    const response = await api.put(`/boards/${id}`, data);
    return response.data.data;
  },

  deleteBoard: async (id: string): Promise<void> => {
    await api.delete(`/boards/${id}`);
  },

  // Lists
  createList: async (data: {
    board_id: string;
    name: string;
    position?: number;
  }): Promise<KanbanList> => {
    const response = await api.post('/lists', data);
    return response.data.data;
  },

  updateList: async (
    id: string,
    data: {
      name?: string;
      position?: number;
      is_archived?: boolean;
    }
  ): Promise<KanbanList> => {
    const response = await api.put(`/lists/${id}`, data);
    return response.data.data;
  },

  deleteList: async (id: string): Promise<void> => {
    await api.delete(`/lists/${id}`);
  },

  reorderLists: async (lists: Array<{ id: string; position: number }>): Promise<void> => {
    await api.put('/lists/reorder', { lists });
  },

  // Cards
  getCard: async (id: string): Promise<KanbanCard> => {
    const response = await api.get(`/cards/${id}`);
    return response.data.data;
  },

  createCard: async (data: {
    list_id: string;
    board_id: string;
    title: string;
    description?: string;
    position?: number;
  }): Promise<KanbanCard> => {
    const response = await api.post('/cards', data);
    return response.data.data;
  },

  updateCard: async (
    id: string,
    data: {
      title?: string;
      description?: string;
      list_id?: string;
      position?: number;
      due_date?: string | null;
      is_completed?: boolean;
      cover_image_url?: string;
      is_archived?: boolean;
      client_id?: string | null;
    }
  ): Promise<KanbanCard> => {
    const response = await api.put(`/cards/${id}`, data);
    return response.data.data;
  },

  moveCard: async (data: {
    card_id: string;
    source_list_id: string;
    destination_list_id: string;
    destination_position: number;
  }): Promise<KanbanCard> => {
    const response = await api.put('/cards/move', data);
    return response.data.data;
  },

  deleteCard: async (id: string): Promise<void> => {
    await api.delete(`/cards/${id}`);
  },

  // Members
  addCardMember: async (cardId: string, userId: string): Promise<void> => {
    await api.post(`/cards/${cardId}/members`, { user_id: userId });
  },

  removeCardMember: async (cardId: string, userId: string): Promise<void> => {
    await api.delete(`/cards/${cardId}/members/${userId}`);
  },

  // Labels
  getLabels: async (boardId: string): Promise<KanbanLabel[]> => {
    const response = await api.get(`/boards/${boardId}/labels`);
    return response.data.data || [];
  },

  createLabel: async (boardId: string, data: { name: string; color: string }): Promise<KanbanLabel> => {
    const response = await api.post(`/boards/${boardId}/labels`, data);
    return response.data.data;
  },

  updateLabel: async (labelId: string, data: { name?: string; color?: string }): Promise<KanbanLabel> => {
    const response = await api.put(`/labels/${labelId}`, data);
    return response.data.data;
  },

  deleteLabel: async (labelId: string): Promise<void> => {
    await api.delete(`/labels/${labelId}`);
  },

  addCardLabel: async (cardId: string, labelId: string): Promise<void> => {
    await api.post(`/cards/${cardId}/labels`, { label_id: labelId });
  },

  removeCardLabel: async (cardId: string, labelId: string): Promise<void> => {
    await api.delete(`/cards/${cardId}/labels/${labelId}`);
  },

  // Checklists
  createChecklist: async (
    cardId: string,
    data: { title: string; items?: string[] }
  ): Promise<KanbanChecklist> => {
    const response = await api.post(`/cards/${cardId}/checklists`, data);
    return response.data.data;
  },

  updateChecklistItem: async (
    checklistId: string,
    itemId: string,
    data: { text?: string; is_completed?: boolean; position?: number }
  ): Promise<KanbanChecklistItem> => {
    const response = await api.put(`/checklists/${checklistId}/items/${itemId}`, data);
    return response.data.data;
  },

  addChecklistItem: async (checklistId: string, text: string): Promise<KanbanChecklistItem> => {
    const response = await api.post(`/checklists/${checklistId}/items`, { text });
    return response.data.data;
  },

  deleteChecklistItem: async (checklistId: string, itemId: string): Promise<void> => {
    await api.delete(`/checklists/${checklistId}/items/${itemId}`);
  },

  deleteChecklist: async (checklistId: string): Promise<void> => {
    await api.delete(`/checklists/${checklistId}`);
  },

  // Comments
  addComment: async (cardId: string, text: string): Promise<KanbanComment> => {
    const response = await api.post(`/cards/${cardId}/comments`, { text });
    return response.data.data;
  },

  updateComment: async (commentId: string, text: string): Promise<KanbanComment> => {
    const response = await api.put(`/comments/${commentId}`, { text });
    return response.data.data;
  },

  deleteComment: async (commentId: string): Promise<void> => {
    await api.delete(`/comments/${commentId}`);
  },

  // Attachments
  uploadAttachment: async (cardId: string, file: File): Promise<KanbanAttachment> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post(`/cards/${cardId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  deleteAttachment: async (attachmentId: string): Promise<void> => {
    await api.delete(`/attachments/${attachmentId}`);
  },
};

export default kanbanService;
